import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { taskKeys } from "@/domains/tasks/api/keys";
import type { CameraTask, TaskStatus, TaskTemplate } from "@/domains/tasks/api/types";
import {
  addCustomTask,
  createTaskTemplate,
  deleteTask,
  deleteTaskTemplate,
  generateTasksFromTemplate,
  saveTaskTemplate,
  updateTask,
  updateTaskDueDate,
  updateTaskStatus,
} from "@/domains/tasks/mocks/tasksService";

type StatusVars = { id: string; status: TaskStatus };

export function useUpdateTaskStatus(): UseMutationResult<CameraTask, Error, StatusVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: StatusVars) => updateTaskStatus(id, status),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

type DueDateVars = { id: string; dueDate: string };

export function useUpdateTaskDueDate(): UseMutationResult<
  CameraTask,
  Error,
  DueDateVars,
  { snapshots: Array<[ReadonlyArray<unknown>, ReadonlyArray<CameraTask> | undefined]> }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dueDate }: DueDateVars) => updateTaskDueDate(id, dueDate),
    onMutate: async ({ id, dueDate }) => {
      await client.cancelQueries({ queryKey: taskKeys.all });
      const matches = client.getQueriesData<ReadonlyArray<CameraTask>>({
        queryKey: taskKeys.all,
      });
      const snapshots: Array<
        [ReadonlyArray<unknown>, ReadonlyArray<CameraTask> | undefined]
      > = [];
      for (const [key, value] of matches) {
        if (!Array.isArray(value)) continue;
        snapshots.push([key, value]);
        client.setQueryData<ReadonlyArray<CameraTask>>(
          key,
          value.map((t) => (t.id === id ? { ...t, dueDate } : t)),
        );
      }
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, value] of context.snapshots) {
        client.setQueryData(key, value);
      }
    },
    onSettled: () => {
      client.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

type GenerateVars = {
  caseId: string;
  templateId: string;
  startDate: string;
  defaultAssignee: { userId: string; name: string };
  partnerAssignee: { userId: string; name: string };
};

export function useGenerateTasksFromTemplate(): UseMutationResult<
  ReadonlyArray<CameraTask>,
  Error,
  GenerateVars
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      templateId,
      startDate,
      defaultAssignee,
      partnerAssignee,
    }: GenerateVars) =>
      generateTasksFromTemplate(
        caseId,
        templateId,
        startDate,
        defaultAssignee,
        partnerAssignee,
      ),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useAddCustomTask(): UseMutationResult<CameraTask, Error, Omit<CameraTask, "id">> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: addCustomTask,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

type UpdateTaskVars = {
  id: string;
  patch: Partial<Omit<CameraTask, "id" | "caseId">>;
};

export function useUpdateTask(): UseMutationResult<CameraTask, Error, UpdateTaskVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: UpdateTaskVars) => updateTask(id, patch),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useDeleteTask(): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: deleteTask,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useSaveTaskTemplate(): UseMutationResult<TaskTemplate, Error, TaskTemplate> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: saveTaskTemplate,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: taskKeys.templates() });
    },
  });
}

export function useCreateTaskTemplate(): UseMutationResult<
  TaskTemplate,
  Error,
  Omit<TaskTemplate, "id">
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createTaskTemplate,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: taskKeys.templates() });
    },
  });
}

export function useDeleteTaskTemplate(): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: deleteTaskTemplate,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: taskKeys.templates() });
    },
  });
}
